import { useFormik } from "formik";
import { toast } from "react-toastify";
import ModalComponent from "..";
import { CreateTicket } from "../../../redux/features/event/event.types";
import { FEE_PERCENTAGE_TEXT } from "../../../utils/constants";
import {
  formatNumber,
  formatToNaira,
  parseNairaToNumber,
} from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { EventTicketSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import Dropdown from "../../dropdown/dropdown";
import { CaretDown } from "../../icon/downcaret";
import { TextInput } from "../../inputs/textInput";
import { TabNav } from "../../tabNav";

export const EventTicketModal = ({
  open,
  handleClose,
  ticket,
  selectedIdx,
  tickets,
  onSuccess = () => {},
  returnMode = "array",
  loading = false,
}: ModalCompProps & {
  ticket?: CreateTicket;
  selectedIdx?: number;
  tickets: CreateTicket[];
  onSuccess: (tickets: CreateTicket[] | CreateTicket) => void;
  returnMode?: "array" | "singleValue";
  loading?: boolean;
}) => {
  const {
    handleSubmit,
    values,
    touched,
    handleBlur,
    handleChange,
    errors,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      type: ticket?.ticketType ?? "paid",
      name: ticket?.name ?? "",
      description: ticket?.description ?? "",
      price: ticket?.price ?? "0",
      quantityType: ticket?.quantityType ?? "limited",
      quantity: ticket?.quantityAvailable ?? "0",
    },
    enableReinitialize: true,
    validationSchema: EventTicketSchema,
    onSubmit: async (vals) => {
      const newTicket: CreateTicket = {
        name: vals.name,
        ticketType: vals.type,
        price: parseNairaToNumber(String(values.price)) ?? 0,
        description: vals.description !== "" ? vals.description : undefined,
        quantityType: vals.quantityType,
        quantityAvailable:
          vals.quantityType === "unlimited"
            ? 0
            : (parseNairaToNumber(String(vals.quantity)) ?? 0),
      };

      if (returnMode === "array") {
        const ticketsCopy = JSON.parse(JSON.stringify(tickets));

        if (ticket && selectedIdx !== undefined) {
          ticketsCopy[selectedIdx!] = newTicket;
        } else {
          ticketsCopy.push(newTicket);
          toast.success("Ticket Created");
        }

        onSuccess(ticketsCopy);
        resetForm();

        close();
      } else {
        onSuccess(newTicket);
        resetForm();
      }
    },
  });

  const close = () => {
    resetForm();
    handleClose();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={close}
      className="!p-"
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={ticket ? "Edit Ticket" : "Create Ticket"}
      subtitle={
        ticket
          ? "These changes would only apply to new tickets that are being sold. Tickets that have already been purchased won’t be affected."
          : `We don't charge fees for free tickets. For paid tickets, we charge a ${FEE_PERCENTAGE_TEXT} per ticket and this fee is passed to the attendees by default.`
      }
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form className="grid grid-cols-2 items-start gap-4">
        <TabNav
          navs={[
            {
              title: "Paid",
              to: "paid",
              onClick: (selected) => {
                setFieldValue("price", "0");
                setFieldValue("type", selected);
              },
            },
            {
              title: "Free",
              to: "free",
              onClick: (selected) => {
                setFieldValue("price", "0");
                setFieldValue("type", selected);
              },
            },
          ]}
          activeTab={values.type}
          variant="switcher"
          tabClassName=""
          className="col-span-full"
        />

        <TextInput
          type="text"
          name="name"
          value={values.name}
          placeholder="Enter ticket name"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Ticket Name"
          errors={errors.name && touched.name ? errors.name : ""}
          containerClassName="col-span-full"
        />

        <TextInput
          type="textarea"
          name="description"
          value={values.description}
          placeholder="Enter description"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Description (Optional)"
          errors={
            errors.description && touched.description ? errors.description : ""
          }
          containerClassName="col-span-full"
          rows={1}
        />

        {values.type === "paid" && (
          <TextInput
            type="text"
            name="price"
            value={formatToNaira(values.price)}
            placeholder="Enter ticket price"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Price"
            errors={errors.price && touched.price ? errors.price : ""}
            containerClassName="col-span-full"
          />
        )}

        <Dropdown
          menuItems={[
            {
              name: "Unlimited",
              value: "Unlimited",
              onClick: () => {
                setFieldValue("quantity", Infinity);
                setFieldValue("quantityType", "unlimited");
              },
            },
            {
              name: "Limited",
              value: "Limited",
              onClick: () => {
                setFieldValue("quantity", "0");
                setFieldValue("quantityType", "limited");
              },
            },
          ]}
          position={"bottom start"}
          menuClassName="translate-x-[-12px] !z-[999]"
          menuItemsClassName=""
        >
          <TextInput
            type="text"
            name="quantityType"
            value={values.quantityType}
            placeholder="Select quantity type"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Quantity Type"
            readOnly
            customIcon={<CaretDown />}
            customIconPosition="right"
            errors={
              errors.quantityType && touched.quantityType
                ? errors.quantityType
                : ""
            }
            inputClass="capitalize"
          />
        </Dropdown>

        <TextInput
          type="text"
          name="quantity"
          value={formatNumber(values.quantity ?? "")}
          placeholder="Enter ticket quantity"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Quantity"
          readOnly={values.quantityType === "unlimited"}
          errors={errors.quantity && touched.quantity ? errors.quantity : ""}
        />

        <div className="col-span-full grid w-full grid-cols-2 items-center justify-end gap-2 md:flex">
          <Button
            text="Cancel"
            type="button"
            onClick={() => {
              close();
            }}
            variation="plain-grey"
            className="md:max-w-fit"
          />
          <Button
            text={ticket !== undefined ? "Save Changes" : "Create"}
            type="button"
            onClick={() => {
              handleSubmit();
            }}
            className="md:max-w-fit"
            loading={loading}
            disabled={loading}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
